import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import { createFileRoute, Link, Outlet, redirect } from "@tanstack/react-router"
import { lazy, Suspense, useEffect, useState } from "react"
import { FiFileText, FiPackage, FiUsers } from "react-icons/fi"
import { GrBundle } from "react-icons/gr"
import LicenseAlertBanner from "../components/Common/LicenseAlertBanner"
import LicenseLimitModal from "../components/Common/LicenseLimitModal"
import LicenseModuleRouteBoundary from "../components/Common/LicenseModuleRouteBoundary"
import SystemNotifications from "../components/Common/SystemNotifications"
import { releaseNotesHistory } from "../content/releaseNotes"
import {
  addReleaseNotesReadListener,
  hasUnreadReleaseNote,
} from "../utils/releaseNotesReadState"

const Sidebar = lazy(() => import("../components/Common/Sidebar"))
const UserMenu = lazy(() => import("../components/Common/UserMenu"))
const ThemeToggleButton = lazy(() => import("../utils/ThemeToggleButton"))

import useAuth, { isLoggedIn } from "../hooks/useAuth"

export const Route = createFileRoute("/_layout")({
  component: Layout,
  beforeLoad: async () => {
    if (!isLoggedIn()) {
      throw redirect({ to: "/login" })
    }
  },
})

function Layout() {
  const { isLoading } = useAuth()
  const topBarButtonBg = useColorModeValue("white", "gray.800")
  const topBarButtonHoverBg = useColorModeValue("gray.100", "gray.700")
  const topBarButtonActiveBg = useColorModeValue("gray.200", "gray.600")
  const topBarButtonColor = useColorModeValue("gray.800", "gray.100")
  const topBarButtonShadow = useColorModeValue("sm", "dark-lg")
  const topBarMutedColor = useColorModeValue("gray.600", "gray.300")
  const latestRelease = releaseNotesHistory[0]
  const [hasUnreadLatestRelease, setHasUnreadLatestRelease] = useState(() =>
    hasUnreadReleaseNote(latestRelease),
  )
  const quickLinks = [
    {
      label: "Kontrahenci",
      to: "/WMS/contragents/contragents-list" as const,
      icon: FiUsers,
    },
    {
      label: "Produkty",
      to: "/WMS/products/products-list" as const,
      icon: FiPackage,
    },
    {
      label: "Magazyny",
      to: "/WMS/warehouses/warehouses-list" as const,
      icon: GrBundle,
    },
    {
      label: "Dokumenty",
      to: "/WMS/documents/document-list" as const,
      icon: FiFileText,
    },
  ]

  useEffect(() => {
    const refreshUnreadState = () => {
      setHasUnreadLatestRelease(hasUnreadReleaseNote(latestRelease))
    }
    refreshUnreadState()
    return addReleaseNotesReadListener(refreshUnreadState)
  }, [])

  return (
    <Suspense fallback={<Spinner size="xl" color="main.accent" />}>
      <Flex
        direction={{ base: "column", md: "row" }}
        h="100vh"
        w="100%"
        overflow="hidden"
        position="relative"
      >
        {/* Sidebar — займає фіксовану ширину */}
        <Sidebar />

        {/* Основний контент */}
        <Box
          flex="1"
          overflowX="auto"
          overflowY="auto"
          px={{ base: 4, md: "15px" }}
          py={{ base: 4, md: 6 }}
          mt="60px"
          position="relative"
          zIndex="1"
        >
          {isLoading ? (
            <Flex justify="center" align="center" height="100vh" width="full">
              <Spinner size="xl" color="main.accent" />
            </Flex>
          ) : (
            <>
              <LicenseAlertBanner />
              <LicenseLimitModal />
              <LicenseModuleRouteBoundary>
                <Outlet />
              </LicenseModuleRouteBoundary>
            </>
          )}
        </Box>
        {/* Меню користувача */}
        {/* Кнопка перемикання теми — праворуч, поруч із меню користувача */}
        <Flex
          display={{ base: "none", xl: "flex" }}
          position="fixed"
          top={4}
          left="calc(var(--desktop-sidebar-offset, 280px) + 16px)"
          right={4}
          zIndex="1000"
          align="flex-start"
          justify="space-between"
          gap={4}
        >
          <HStack spacing={2} flexShrink={0}>
            {quickLinks.map((item) => (
              <Button
                key={item.to}
                as={Link}
                to={item.to}
                variant="ghost"
                h={{ xl: "52px", "2xl": "56px" }}
                minW={{ xl: "60px", "2xl": "72px" }}
                px={{ xl: 2.5, "2xl": 3 }}
                borderRadius="2xl"
                bg={topBarButtonBg}
                color={topBarButtonColor}
                _hover={{ bg: topBarButtonHoverBg }}
                _active={{ bg: topBarButtonActiveBg }}
                boxShadow={topBarButtonShadow}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
              >
                <Icon as={item.icon} boxSize={4} />
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  lineHeight="1"
                  color={topBarMutedColor}
                >
                  {item.label}
                </Text>
              </Button>
            ))}
          </HStack>
          <HStack spacing={2} flexShrink={0}>
            <Button
              as={Link}
              to="/developer-profile"
              variant="ghost"
              h={{ xl: "52px", "2xl": "56px" }}
              minW={{ xl: "60px", "2xl": "72px" }}
              px={{ xl: 2.5, "2xl": 3 }}
              borderRadius="2xl"
              bg={topBarButtonBg}
              color={topBarButtonColor}
              _hover={{ bg: topBarButtonHoverBg }}
              _active={{ bg: topBarButtonActiveBg }}
              boxShadow={topBarButtonShadow}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              position="relative"
              gap={0.5}
            >
              {hasUnreadLatestRelease ? (
                <Badge
                  position="absolute"
                  top="-6px"
                  right="-6px"
                  colorScheme="red"
                  borderRadius="full"
                  px={2}
                  fontSize="10px"
                  lineHeight="1.5"
                  boxShadow="sm"
                >
                  Nowe
                </Badge>
              ) : null}
              <Image
                src="/assets/images/stockerra_logo.png"
                alt="Strona informacji o producencie Stockerra"
                h="18px"
                objectFit="contain"
              />
              <Text
                fontSize="xs"
                fontWeight="600"
                lineHeight="1"
                color={topBarMutedColor}
              >
                Stockerra
              </Text>
            </Button>
            <SystemNotifications mode="tile" />
            <ThemeToggleButton mode="tile" />
            <UserMenu inline />
          </HStack>
        </Flex>
      </Flex>
    </Suspense>
  )
}
